import type { NodeData } from "@/workflow_api/types/workflow";
import type { NodeVariable, Variable } from "../../types";
import type { DocumentField, DocumentTemplate } from "../types";
import { documentTemplates } from "../types";
import type { VariableFileName } from "../context";

/**
 * Tipos para inputs serializados (siguiendo el patrón de calculated-field)
 */
interface SerializedInput {
  name: string;
  value: string | number | boolean | null;
  type: string;
  connection?: string;
  order: number;
  field?: string;
  output_name?: string;
  global_type?:
    | "APPLICANT"
    | "NODE_EXECUTION_START"
    | "NODE_EXECUTION_FINISH"
    | "CURRENT_TIME";
  source_connections?: Array<{
    id: string;
    target_node_id: string;
    source_node_id: string;
    target_node_input_id: string;
    field_id?: string | null;
    output_name?: string | null;
  }>;
  target_connections?: Array<unknown>;
}

/**
 * Mapeo de tipos del backend al frontend para VariableType
 */
const mapBackendTypeToVariableType = (
  backendType: string,
): "string" | "number" | "date" | "boolean" | "array" => {
  switch (backendType.toUpperCase()) {
    case "STRING":
    case "TEXT":
      return "string";
    case "INTEGER":
    case "NUMBER":
    case "FLOAT":
    case "DOUBLE":
      return "number";
    case "DATE":
    case "DATETIME":
      return "date";
    case "BOOLEAN":
    case "BOOL":
      return "boolean";
    case "ARRAY":
    case "FORM":
    case "MIXED":
      return "array";
    default:
      return "string";
  }
};

/**
 * Mapeo de tipos del backend al frontend para DocumentField
 */
const mapBackendTypeToVariable = (
  backendType: string,
): "text" | "number" | "date" | "boolean" => {
  switch (backendType.toUpperCase()) {
    case "STRING":
    case "TEXT":
      return "text";
    case "INTEGER":
    case "NUMBER":
    case "FLOAT":
    case "DOUBLE":
      return "number";
    case "DATE":
    case "DATETIME":
      return "date";
    case "BOOLEAN":
    case "BOOL":
      return "boolean";
    default:
      return "text";
  }
};

/**
 * Maps field types to the appropriate type for DocumentField
 * Updated to handle the API response types: STRING, INTEGER, NUMBER, FLOAT, DATE, BOOLEAN, MIXED, FORM, ARRAY
 */
const mapTypeToDocumentFieldType = (
  type: string,
): "text" | "number" | "date" | "boolean" | "variable" => {
  switch (type.toUpperCase()) {
    case "STRING":
    case "TEXT":
    case "VARCHAR":
    case "CHAR":
      return "text";
    case "NUMBER":
    case "INT":
    case "INTEGER":
    case "FLOAT":
    case "DOUBLE":
    case "DECIMAL":
      return "number";
    case "DATE":
    case "DATETIME":
    case "TIMESTAMP":
      return "date";
    case "BOOLEAN":
    case "BOOL":
      return "boolean";
    case "MIXED":
    case "FORM":
    case "ARRAY":
      return "text"; // Por ahora manejar estos como texto
    default:
      return "text"; // fallback to text for unknown types
  }
};

/**
 * Mapeo de tipos del API al tipo de campo para crear campos desde template
 */
const mapApiTypeToFieldType = (
  apiType: string,
): "text" | "number" | "date" | "boolean" => {
  switch (apiType.toUpperCase()) {
    case "STRING":
    case "TEXT":
      return "text";
    case "INTEGER":
    case "NUMBER":
    case "FLOAT":
    case "DOUBLE":
      return "number";
    case "DATE":
    case "DATETIME":
      return "date";
    case "BOOLEAN":
    case "BOOL":
      return "boolean";
    default:
      return "text";
  }
};

/**
 * Get placeholder text based on field type
 */
const getPlaceholderForType = (type: string): string => {
  switch (type) {
    case "text":
      return "Escribe un texto";
    case "number":
      return "Escribe un número";
    case "date":
      return "Selecciona una fecha";
    case "boolean":
      return "Elige verdadero o falso";
    case "variable":
      return "Elige una variable";
    default:
      return "Ingresa un valor";
  }
};

/**
 * Formatea un valor para mostrar en el frontend
 */
const formatValueForFrontend = (
  value: string | number | boolean | null,
  variableType: string,
): string => {
  if (value === null || value === undefined) {
    return "";
  }

  switch (variableType) {
    case "number":
      return typeof value === "number" ? value.toString() : String(value);
    case "boolean":
      return typeof value === "boolean" ? value.toString() : String(value);
    case "date":
      return String(value);
    default:
      return String(value);
  }
};

/**
 * Deserialize fields data from the node using the new input structure
 * Similar to calculated-field deserialization but adapted for documents
 *
 * @param node - The API node containing the serialized data
 * @param inputVariables - Input variables for resolving variable references
 * @param selectedTemplate - Template to recreate fields if no serialized fields found
 * @returns Array of DocumentField objects
 */
export const getDeserializedFields = (
  node: NodeData,
  inputVariables: NodeVariable[],
  selectedTemplate?: DocumentTemplate | null,
): DocumentField[] => {
  if (!node.inputs) {
    // Si no hay inputs pero hay una plantilla, crear campos desde la plantilla
    if (selectedTemplate?.variables && selectedTemplate.variables.length > 0) {
      return selectedTemplate.variables.map((variable) => {
        const fieldType = mapApiTypeToFieldType(variable.type || "STRING");
        return {
          name: variable.mapping_name,
          label: variable.display_name,
          type: fieldType,
          required: false,
          value: "",
          placeholder: getPlaceholderForType(fieldType),
          inputType: "input" as const,
        };
      });
    }
    return [];
  }

  try {
    // Lista de campos de metadata que deben ser excluidos
    const metadataFields = [
      "template_id",
      "template_name",
      "template_version",
      "file_name",
      "output_name",
    ];

    // Filtrar solo los inputs que son campos (excluyendo metadata)
    const fieldInputs = node.inputs.filter(
      (input) => input.name && !metadataFields.includes(input.name),
    ) as SerializedInput[];

    // Ordenar por order si está disponible
    const sortedInputs = fieldInputs.sort(
      (a, b) => (a.order || 0) - (b.order || 0),
    );

    // Get flat list of all input variables
    const allInputVariables = inputVariables.flatMap(
      (node) =>
        (node.variables || [])
          .map((v) => ({
            ...v,
            node_id: node.nodeId,
          }))
          .filter((v) => v.id && v.id.trim() !== ""), // Filtrar variables con IDs vacíos o inválidos
    );

    const fields: DocumentField[] = [];

    sortedInputs.forEach((input, index) => {
      let value: string | Variable = "";
      let inputType: "input" | "variable" = "input";

      const fieldName = input.name || `field_${index}`;
      if (input.global_type && input.value) {
        inputType = "variable";
        const nodeId = input.value as string;

        const globalVariable = allInputVariables.find((v) => {
          if (
            input?.global_type === "APPLICANT" ||
            input?.global_type === "CURRENT_TIME"
          ) {
            return v.id === nodeId && v.global_type === input.global_type;
          } else {
            return v.node_id === nodeId && v.global_type === input.global_type;
          }
        });

        if (globalVariable) {
          value = globalVariable;
        } else {
          if (
            input.global_type === "APPLICANT" ||
            input.global_type === "CURRENT_TIME"
          ) {
            value = {
              id: input.value as string,
              name: "",
              type: mapBackendTypeToVariableType(input.type),
              node_id: "",
              global_type: input.global_type,
            };
          } else {
            const idSuffix =
              input.global_type === "NODE_EXECUTION_START"
                ? "execution_start"
                : "execution_finish";
            value = {
              id: `${nodeId}_${idSuffix}`,
              name:
                input.global_type === "NODE_EXECUTION_START"
                  ? "inicio-nodo"
                  : "fin-nodo",
              type: mapBackendTypeToVariableType(input.type),
              node_id: nodeId,
              global_type: input.global_type,
            };
          }
        }
      } else {
        const hasSourceConnection =
          input.source_connections && input.source_connections.length > 0;
        const sourceConnection = hasSourceConnection
          ? input.source_connections![0]
          : null;
        const isConnectedVariable = hasSourceConnection || input.connection;

        if (isConnectedVariable) {
          inputType = "variable";

          if (hasSourceConnection && sourceConnection) {
            if (sourceConnection.output_name && sourceConnection.field_id) {
              const variable = allInputVariables.find(
                (v) =>
                  v.id ===
                  `${sourceConnection.field_id}-${sourceConnection.output_name}`,
              );

              if (variable) {
                value = variable;
              }
            } else if (sourceConnection.field_id) {
              const variable = allInputVariables.find(
                (v) => v.id === sourceConnection.field_id,
              );
              if (variable) {
                value = variable;
              } else {
                value = {
                  id: sourceConnection.field_id,
                  name: sourceConnection.field_id,
                  type: mapBackendTypeToVariableType(input.type),
                  node_id: sourceConnection.source_node_id,
                };
              }
            } else if (sourceConnection.output_name) {
              // Try ID-based match first (works for interoperability/n8n nodes)
              let variable = allInputVariables.find(
                (v) =>
                  v.node_id === sourceConnection.source_node_id &&
                  v.id ===
                    `${sourceConnection.source_node_id}-${sourceConnection.output_name}`,
              );
              // Fallback: match by output_name property (for expediente nodes where ID uses field.id)
              if (!variable) {
                variable = allInputVariables.find(
                  (v) =>
                    v.node_id === sourceConnection.source_node_id &&
                    v.output_name === sourceConnection.output_name,
                );
              }
              if (variable) {
                value = variable;
              } else {
                value = {
                  id: `${sourceConnection.source_node_id}-${sourceConnection.output_name}`,
                  name: sourceConnection.output_name,
                  type: mapBackendTypeToVariableType(input.type),
                  node_id: sourceConnection.source_node_id,
                };
              }
            } else {
              const variable = allInputVariables.find(
                (v) =>
                  v.node_id === sourceConnection.source_node_id &&
                  v.id === v.node_id,
              );
              if (variable) {
                value = variable;
              } else {
                value = {
                  id: sourceConnection.source_node_id,
                  name: sourceConnection.source_node_id,
                  type: mapBackendTypeToVariableType(input.type),
                  node_id: sourceConnection.source_node_id,
                };
              }
            }
          } else {
            if (input.field) {
              const variable = allInputVariables.find(
                (v) => v.id === input.field,
              );
              if (variable) {
                value = variable;
              } else {
                value = {
                  id: input.field,
                  name: input.field,
                  type: mapBackendTypeToVariableType(input.type),
                  node_id: input.connection || "",
                };
              }
            } else if (input.output_name) {
              // Para nodos expediente/interop: buscar por output_name
              const variable = allInputVariables.find(
                (v) =>
                  v.node_id === input.connection &&
                  v.output_name === input.output_name,
              );
              if (variable) {
                value = variable;
              } else {
                const conn = input.connection || "";
                value = {
                  id: conn ? `${conn}-${input.output_name}` : input.output_name,
                  name: input.output_name,
                  type: mapBackendTypeToVariableType(input.type),
                  node_id: conn,
                };
              }
            } else {
              const variable = allInputVariables.find(
                (v) => v.node_id === input.connection && v.id === v.node_id,
              );
              if (variable) {
                value = variable;
              } else {
                value = {
                  id: input.connection || "",
                  name: input.connection || "",
                  type: mapBackendTypeToVariableType(input.type),
                  node_id: input.connection || "",
                };
              }
            }
          }
        } else {
          value = formatValueForFrontend(
            input.value,
            mapBackendTypeToVariable(input.type),
          );
          inputType = "input";
        }
      }

      const fieldType = mapTypeToDocumentFieldType(input.type);

      const field: DocumentField = {
        name: fieldName,
        label: fieldName,
        type: fieldType,
        required: false, // Default to not required
        value: value,
        placeholder: getPlaceholderForType(fieldType),
        inputType,
      };
      fields.push(field);
    });

    // Si no hay campos serializados pero hay una plantilla, crear campos desde la plantilla
    if (
      fields.length === 0 &&
      selectedTemplate?.variables &&
      selectedTemplate.variables.length > 0
    ) {
      return selectedTemplate.variables.map((variable) => {
        const fieldType = mapApiTypeToFieldType(variable.type || "STRING");
        return {
          name: variable.mapping_name,
          label: variable.display_name,
          type: fieldType,
          required: false,
          value: "",
          placeholder: getPlaceholderForType(fieldType),
          inputType: "input" as const,
        };
      });
    }

    return fields;
  } catch {
    return [];
  }
};

/**
 * Get the selected template from the node data
 * Updated to work with the new input structure
 *
 * @param node - The API node containing the template information
 * @returns The selected DocumentTemplate or null if not found
 */
export const getSelectedTemplate = (
  node: NodeData,
): DocumentTemplate | null => {
  const templateIdInput = node.inputs?.find(
    (input) => input.name === "template_id",
  );
  const templateNameInput = node.inputs?.find(
    (input) => input.name === "template_name",
  );

  const templateVersionInput = node.inputs?.find(
    (input) => input.name === "template_version",
  );

  if (!templateIdInput?.value) {
    return null;
  }

  const templateId = templateIdInput.value as string;
  const templateName = templateNameInput?.value as string;
  const templateVersion = templateVersionInput?.value as number;

  // First try to find in hardcoded templates (for backward compatibility)
  const hardcodedTemplate = documentTemplates.find(
    (template) => template.id === templateId,
  );
  if (hardcodedTemplate) {
    return hardcodedTemplate;
  }

  // If not found in hardcoded templates, create a basic template structure
  // This will be matched later against API templates in the context
  return {
    id: templateId,
    name: templateName || templateId,
    description: "Template cargado del servidor",
    version: templateVersion,
  };
};

/**
 * Get the file name from the node data
 * Updated to work with the new input structure
 *
 * @param node - The API node containing the file name
 * @returns The file name or empty string if not found
 */
export const getFileName = (node: NodeData): string => {
  const fileNameInput = node.inputs?.find(
    (input) => input.name === "file_name",
  );
  return (fileNameInput?.value as string) || "";
};

/**
 * Get the variable name from the node data
 * Updated to work with the new input structure
 *
 * @param node - The API node containing the variable name
 * @returns The variable name or empty string if not found
 */
export const getVariableName = (node: NodeData): VariableFileName => {
  const variableNameInput = node.inputs?.find(
    (input) => input.name === "output_name",
  );

  if (!variableNameInput?.value) {
    return {
      base: "",
      include_request_id: false,
      include_date: false,
    };
  }

  const fileName = JSON.parse(variableNameInput?.value as string) || {
    base: "",
    include_request_id: false,
    include_date: false,
  };

  return fileName;
};

/**
 * Deserialize the complete document generator node
 *
 * @param node - The API node to deserialize
 * @param inputVariables - Input variables for resolving variable references
 * @param apiTemplates - Available templates from API to recreate fields if needed
 * @returns Object containing all deserialized data
 */
export const getDeserializedDocumentGeneratorNode = (
  node: NodeData,
  inputVariables: NodeVariable[],
  apiTemplates?: DocumentTemplate[],
): {
  selectedTemplate: DocumentTemplate | null;
  fileName: string;
  fields: DocumentField[];
  variableName: VariableFileName;
} => {
  const selectedTemplate = getSelectedTemplate(node);

  // Si tenemos plantillas del API, buscar la plantilla completa
  let fullTemplate = selectedTemplate;
  if (selectedTemplate && apiTemplates) {
    const apiTemplate = apiTemplates.find((t) => {
      if (selectedTemplate.version !== undefined) {
        return (
          t.id === selectedTemplate.id && t.version === selectedTemplate.version
        );
      }
      return t.id === selectedTemplate.id;
    });

    if (apiTemplate) {
      fullTemplate = apiTemplate;
    }
  }

  return {
    selectedTemplate: fullTemplate,
    fileName: getFileName(node),
    fields: getDeserializedFields(node, inputVariables, fullTemplate),
    variableName: getVariableName(node),
  };
};
